import { useFile } from "#src/hooks/useFile";
import { useNavigate, useSearchParams } from "#src/Routers/hooks";
import { WorkflowTasksRoutePath } from "#src/routes/workflows/tasks";
import { useAnnotationStore } from "#src/stores/annotationStore";
import { ExceptionUtils } from "#src/utils/exception";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Banner,
  Button,
  Card,
  Dialog,
  DropdownMenu,
  Footer,
  Icon,
  KeyValueList,
  Page,
  Stepper,
  useToast,
} from "@validereinc/common-components";
import {
  FormSubmissionAdapter,
  Resources,
  WorkflowTaskAdapter,
} from "@validereinc/domain";
import {
  formatNumberToFixedIfNotInteger,
  HTTPResponseErrorProps,
  toTitleCaseString,
} from "@validereinc/utilities";
import { FabricText, Line, Path } from "fabric";
import { Base64 } from "js-base64";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import AnnotationNumberInput from "./components/AnnotationNumberInput";
import {
  aggregateFunctions,
  backgroundType,
  createPathFromPoints,
  directionType,
  measurementKeys,
  measurementsType,
  previewOnlyAttributes,
  resetView,
  useHydrateStoreFromInitialValues,
  usePrepareFormSubmissionAnswers,
  ValueType,
  wait,
} from "./FabricCTR.helpers";
import { AnnotationDetailRoutePath } from "./index";
import NewAnnotationTool from "./NewAnnotationTool";

const directionKeys: directionType[] = ["left", "right"];

const markingsPrefix = "marking-";

const markingPrefixGenerator = (
  direction: directionType,
  background: backgroundType
) => `${markingsPrefix}${direction}-${background}-`;

const MEASUREMENT_ERROR_LABEL_ID = "measurement-error-label";

/** Order for displaying measurement names in the error label (sediment, water, interface). */
const MEASUREMENT_LABEL_ORDER: measurementsType[] = [
  "sediment",
  "water",
  "interface",
];

/**
 * Returns the measurement error label for a direction, or null if no measurements have error.
 * Order of names: Sediment, Water, Interface (capitalized).
 */
const getMeasurementErrorLabelForDirection = (
  direction: directionType,
  measurements: Record<`${directionType}_${measurementsType}`, ValueType>
): string | null => {
  const errorMeasurements = MEASUREMENT_LABEL_ORDER.filter(
    (key) => measurements[`${direction}_${key}`] === "error"
  ).map((key) => toTitleCaseString(key));

  if (errorMeasurements.length === 0) return null;
  return `Measurement Error: ${errorMeasurements.join(", ")}`;
};

const renderValue = (value: ValueType) => {
  if (value === "error")
    return (
      <div
        style={{
          marginBottom: 0,
          color: "#D83B3E",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Icon
          variant="warning"
          size={20}
          style={{ marginBottom: 0 }}
        />
        Error
      </div>
    );
  if (value === undefined) return "-";
  return formatNumberToFixedIfNotInteger(value, 7);
};

export const NewAnnotationToolPage = () => {
  const [breadcrumbs] = useBreadcrumbsFromRoute(AnnotationDetailRoutePath);

  const navigate = useNavigate();

  // Zustand store
  const {
    measurements,
    updateMeasurement,
    activeMeasurement,
    setActiveMeasurement,
    clearActiveMeasurement,
    activeStep,
    goToNextStep,
    goToPreviousStep,
    resetSteps,
    reset,
    imageURLs,
    setIsLoading,
    fabricCanvas,
    setFinalScreenshot,
    setMeasurementData,
    measurementData,
    setCurrentBackgroundImage,
    finalScreenshots,
    isSaving,
    setIsSaving,
    progressStep,
    setProgressStep,
    setUploadedScreenshotRef,
    uploadedScreenshotRefs,
    submissionError,
    setSubmissionError,
  } = useAnnotationStore();

  const [
    {
      "default-values": serializedEncodedDefaultValues,
      formSchemaId,
      taskId,
      workflowId,
    },
  ] = useSearchParams();

  /** Tracks whether we already retried with an alternative open task to avoid infinite retry. */
  const hasRetriedOpenTaskRef = useRef(false);

  const queryClient = useQueryClient();

  const initialValues = useMemo(() => {
    let value = {} as Record<string, string>;
    if (!serializedEncodedDefaultValues) return value;
    try {
      const decodedDefaultValues = Base64.decode(
        serializedEncodedDefaultValues
      );
      value = JSON.parse(decodedDefaultValues) as Record<string, string>;
    } catch (err) {
      ExceptionUtils.reportException(err, "error", {
        hint: `Default values (URLParam: default-values) couldn't be decoded and parsed: ${serializedEncodedDefaultValues}`,
      });
    }
    return value;
  }, [serializedEncodedDefaultValues]);

  const { prepareFormSubmissionAnswers } = usePrepareFormSubmissionAnswers({
    annotationFormSchemaId: formSchemaId,
    taskId,
    defaultValues: initialValues,
  });

  // Reset the store when the page unmounts
  useEffect(() => () => reset(), []);

  useHydrateStoreFromInitialValues(initialValues);

  // Update isLoading state based on imageURLs
  useEffect(() => {
    setIsLoading(!imageURLs?.regular || !imageURLs?.uv);
  }, [imageURLs, setIsLoading]);

  /**
   * Checks if there's an integrity issue with the measurements.
   * An integrity issue occurs when a side (left or right) has non-error values
   * but all of them are zero. All errors is acceptable, but errors mixed with
   * only zeros is not.
   */
  const hasIntegrityIssue = useMemo(() => {
    return directionKeys.some((direction) => {
      const directionMeasurements = measurementKeys.map(
        (key) => measurements[`${direction}_${key}`]
      );

      // Only check if all values are defined (not undefined)
      const allValuesDefined = directionMeasurements.every(
        (value) => value !== undefined
      );
      if (!allValuesDefined) return false;

      // Get non-error values
      const nonErrorValues = directionMeasurements.filter(
        (value) => value !== "error"
      );

      // If all values are errors, it's acceptable
      if (nonErrorValues.length === 0) return false;

      // If there are non-error values, at least one must be non-zero
      const allNonErrorValuesAreZero = nonErrorValues.every(
        (value) => value === 0
      );

      return allNonErrorValuesAreZero;
    });
  }, [measurements]);

  const isSaveButtonDisabled = useMemo(
    () =>
      Object.values(measurements).some((value) => value === undefined) ||
      hasIntegrityIssue,
    [measurements, hasIntegrityIssue]
  );

  const isNextButtonDisabled = useMemo(
    () =>
      (activeMeasurement?.measurementPoints.length === 0 && activeStep === 1) ||
      (((activeMeasurement?.bottomTickY ?? -1) < 0 ||
        (activeMeasurement?.topTickY ?? -1) < 0) &&
        activeStep === 2),
    [activeStep, activeMeasurement]
  );

  const { toast } = useToast();

  /**
   * Zooms and centers the canvas view on the markings for a specific direction.
   * @param prefix - The prefix to filter marking objects (e.g., "marking-left-regular-")
   * @param direction - The direction to get meniscus points from for X centering
   */
  const zoomIntoMarkings = (prefix: string, direction: directionType) => {
    if (!fabricCanvas) return;

    const directionObjects = fabricCanvas.getObjects().filter((obj) => {
      const id = obj.get("id")?.toString();
      return id?.startsWith(prefix) && !id?.endsWith("-aggregate-line");
    });

    // Early return if no objects found to avoid invalid calculations
    if (directionObjects.length === 0) return;

    let minY = Infinity;
    let maxY = -Infinity;

    directionObjects.forEach((obj) => {
      const bounds = obj.getBoundingRect();
      minY = Math.min(minY, bounds.top);
      maxY = Math.max(maxY, bounds.top + bounds.height);
    });

    // Add some padding
    const padding = 40;
    minY -= padding;
    maxY += padding;

    // Calculate the content dimensions
    const contentHeight = maxY - minY;

    // Get canvas dimensions
    const canvasWidth = fabricCanvas.width;
    const canvasHeight = fabricCanvas.height;

    const zoomY = (canvasHeight * 0.9) / contentHeight;
    const zoom = Math.max(zoomY, 1); // Don't zoom in beyond 1x

    // Collect all meniscus X values for the direction to calculate median
    const allMeniscusXValues: number[] = [];
    measurementKeys.forEach((measurementKey) => {
      const data = measurementData[`${direction}_${measurementKey}`];
      if (data?.measurementPoints && data.measurementPoints.length > 0) {
        data.measurementPoints.forEach((point) => {
          allMeniscusXValues.push(point.x);
        });
      }
    });

    // Calculate median X of meniscus points for X centering
    let contentCenterX: number;
    if (allMeniscusXValues.length > 0) {
      const sortedXValues = [...allMeniscusXValues].sort((a, b) => a - b);
      const mid = Math.floor(sortedXValues.length / 2);
      contentCenterX =
        sortedXValues.length % 2 === 0
          ? (sortedXValues[mid - 1] + sortedXValues[mid]) / 2
          : sortedXValues[mid];
    } else {
      // Fallback to bounding box center if no meniscus points
      let minX = Infinity;
      let maxX = -Infinity;
      directionObjects.forEach((obj) => {
        const bounds = obj.getBoundingRect();
        minX = Math.min(minX, bounds.left);
        maxX = Math.max(maxX, bounds.left + bounds.width);
      });
      contentCenterX = (minX + maxX) / 2;
    }

    const contentCenterY = (minY + maxY) / 2;

    // Calculate the viewport center
    const viewportCenterX = canvasWidth / 2;
    const viewportCenterY = canvasHeight / 2;

    // Calculate the translation needed to center the content
    const translateX = viewportCenterX - contentCenterX * zoom;
    const translateY = viewportCenterY - contentCenterY * zoom;

    // Apply zoom and translation
    fabricCanvas.setZoom(zoom);

    fabricCanvas.setViewportTransform([
      zoom,
      0,
      0,
      zoom,
      translateX,
      translateY,
    ]);
  };

  const generateScreenshotMarkings = async ({
    direction,
    background,
  }: {
    direction: directionType;
    background: backgroundType;
  }) => {
    if (!fabricCanvas) return;

    setCurrentBackgroundImage(background);

    await wait(2000);

    const prefix = markingPrefixGenerator(direction, background);

    // Clear ALL existing marking objects (not just current prefix)
    // This prevents aggregate lines from other directions appearing in the screenshot
    const existingObjects = fabricCanvas
      .getObjects()
      .filter((obj) => obj.get("id")?.toString().startsWith(markingsPrefix));
    existingObjects.forEach((obj) => fabricCanvas.remove(obj));

    const directionMeasurements = measurementKeys
      .map((measurementKey) => ({
        key: `${direction}_${measurementKey}` as `${directionType}_${measurementsType}`,
        measurement: measurementKey,
        data: measurementData[`${direction}_${measurementKey}`],
      }))
      .filter((measurement) => measurement.data);

    // If there are measurements, draw them; otherwise, just take a plain screenshot
    if (directionMeasurements.length > 0) {
      // Color mapping
      const colorMap = {
        sediment: "red",
        water: "blue",
        interface: "green",
      };

      // Draw each measurement
      directionMeasurements.forEach((measurement) => {
        const { data } = measurement;
        if (!data) {
          return;
        }

        const color = colorMap[measurement.measurement];

        // Draw meniscus (original path)
        if (data.measurementPoints && data.measurementPoints.length > 0) {
          // Create path from measurement points
          const pathData = createPathFromPoints(data.measurementPoints);

          const meniscusPath = new Path(pathData, {
            id: `${prefix}${measurement.measurement}-meniscus`,
            stroke: color,
            strokeWidth: 1,
            fill: "",
            ...previewOnlyAttributes,
          });
          fabricCanvas.add(meniscusPath);

          // Draw aggregate line (dashed horizontal line)
          const aggregateY = aggregateFunctions[data.aggregateFunction](
            data.measurementPoints
          );

          const aggregateLine = new Line(
            [0, aggregateY, fabricCanvas.width, aggregateY],
            {
              stroke: color,
              strokeWidth: 1,
              strokeDashArray: [5, 5],
              strokeUniform: true,
              id: `${prefix}${measurement.measurement}-aggregate-line`,
              ...previewOnlyAttributes,
            }
          );
          fabricCanvas.add(aggregateLine);
        }

        // Draw top tick
        if (data.topTickY > -10000 && data.topTickX !== undefined) {
          const topTickLine = new Line(
            [data.topTickX, data.topTickY, data.topTickX + 40, data.topTickY],
            {
              stroke: color,
              strokeWidth: 1,
              strokeUniform: true,
              id: `${prefix}${measurement.measurement}-top-tick`,
              ...previewOnlyAttributes,
            }
          );
          fabricCanvas.add(topTickLine);
        }

        // Draw bottom tick
        if (data.bottomTickY > -10000 && data.bottomTickX !== undefined) {
          const bottomTickLine = new Line(
            [
              data.bottomTickX + 10,
              data.bottomTickY,
              data.bottomTickX + 50,
              data.bottomTickY,
            ],
            {
              stroke: color,
              strokeWidth: 1,
              strokeUniform: true,
              id: `${prefix}${measurement.measurement}-bottom-tick`,
              ...previewOnlyAttributes,
            }
          );
          fabricCanvas.add(bottomTickLine);
        }
      });

      zoomIntoMarkings(prefix, direction);

      // Adjust line thickness to appear as 1px at the current zoom level
      const zoom = fabricCanvas.getZoom();
      const adjustedStrokeWidth = 0.8 / zoom;

      fabricCanvas
        .getObjects()
        .filter((obj) => obj.get("id")?.toString().startsWith(prefix))
        .forEach((obj) => {
          obj.set("strokeWidth", adjustedStrokeWidth);
        });

      fabricCanvas.renderAll();

      // Add value labels on top of each aggregate line, positioned relative to the viewport
      /** Maps measurement types to their abbreviated layer labels for the screenshot */
      const layerAbbreviationMap: Record<measurementsType, string> = {
        sediment: "S",
        water: "W/S",
        interface: "I/W/S",
      };

      const vpt = fabricCanvas.viewportTransform;
      const vptTranslateX = vpt[4];

      directionMeasurements.forEach((measurement) => {
        const { data } = measurement;
        if (!data?.measurementPoints || data.measurementPoints.length === 0)
          return;

        const value = measurements[measurement.key];
        if (typeof value !== "number") return;

        const color = colorMap[measurement.measurement];
        const aggregateY = aggregateFunctions[data.aggregateFunction](
          data.measurementPoints
        );

        const labelText = `${formatNumberToFixedIfNotInteger(value, 7)}% vol (${layerAbbreviationMap[measurement.measurement]})`;
        const labelFontSize = 14;

        // Position 20px from the right edge of the viewport, converted to canvas coordinates
        const labelCanvasX = (fabricCanvas.width - 20 - vptTranslateX) / zoom;

        const aggregateLineLabel = new FabricText(labelText, {
          id: `${prefix}${measurement.measurement}-aggregate-label`,
          fontSize: labelFontSize,
          fill: color,
          left: labelCanvasX,
          top: aggregateY - labelFontSize / zoom - 2 / zoom,
          scaleX: 1 / zoom,
          scaleY: 1 / zoom,
          /** Fabric 7: position by right edge and top edge for viewport-aligned label. */
          originX: "right",
          originY: "top",
          selectable: false,
          evented: false,
        });
        fabricCanvas.add(aggregateLineLabel);
      });

      fabricCanvas.renderAll();
    } else {
      // No measurements - reset to default view for plain image screenshot
      resetView(fabricCanvas);
    }

    // Add measurement error label at top-left if any measurement has error (20px red, independent of zoom)
    const errorLabelText = getMeasurementErrorLabelForDirection(
      direction,
      measurements
    );
    let errorLabelObject: FabricText | null = null;
    if (errorLabelText) {
      const zoom = fabricCanvas.getZoom();
      const vpt = fabricCanvas.viewportTransform;
      const translateX = vpt[4];
      const translateY = vpt[5];
      const topLeftCanvasX = (10 - translateX) / zoom;
      const topLeftCanvasY = (10 - translateY) / zoom;

      errorLabelObject = new FabricText(errorLabelText, {
        id: MEASUREMENT_ERROR_LABEL_ID,
        left: topLeftCanvasX,
        top: topLeftCanvasY,
        /** Fabric 7 defaults origin to center; use left/top so label is at viewport top-left. */
        originX: "left",
        originY: "top",
        fontSize: 20,
        fill: "red",
        scaleX: 1 / zoom,
        scaleY: 1 / zoom,
        selectable: false,
        evented: false,
      });
      fabricCanvas.add(errorLabelObject);
      fabricCanvas.renderAll();
    }

    // Take screenshot of current canvas view
    const screenshot = fabricCanvas.toDataURL({
      format: "jpeg",
      quality: 0.9,
      multiplier: 1,
    });

    if (errorLabelObject) {
      fabricCanvas.remove(errorLabelObject);
      fabricCanvas.renderAll();
    }

    // Save screenshot
    setFinalScreenshot(`${direction}_${background}`, screenshot);
  };

  const { handleFileUpload } = useFile({
    uploadConfig: { maxPolls: 30, pollSleepTime: 2000 },
  });

  const uploadSnapshot = async (
    measurementKey: `${directionType}_${backgroundType}`
  ) => {
    if (!fabricCanvas) return;

    // Get the screenshot for the selected measurement
    const screenshot = finalScreenshots[measurementKey];
    if (!screenshot) {
      setUploadedScreenshotRef(measurementKey, { name: "", ref: "" });
      return;
    }

    // Convert base64 to Blob
    const base64String = screenshot.split(",")[1];
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "image/jpeg" });

    // Create a File object from the Blob with measurement name
    const fileName = `annotation_${uuidv4()}_${measurementKey}.jpg`;
    const file = new File([blob], fileName, {
      type: "image/jpeg",
    });

    // Use the useFile hook's handleFileUpload function
    const result = await handleFileUpload(file, {
      setStatus: () => undefined,
      signal: null,
    });
    if (result) {
      setUploadedScreenshotRef(measurementKey, result);
    }
  };

  const steps: Array<
    "generating" | "uploading_images" | "uploading_data" | "done"
  > = ["generating", "uploading_images", "uploading_data", "done"];

  const activeStepOfSubmission = steps.indexOf(progressStep) + 1;

  useEffect(() => {
    if (progressStep !== "generating") return;
    if (Object.values(finalScreenshots).filter((v) => v !== null).length !== 4)
      return;

    setProgressStep("uploading_images");
  }, [finalScreenshots, progressStep]);

  useEffect(() => {
    if (progressStep !== "uploading_images") return;

    const performUploads = async () => {
      try {
        const fileUploads = Object.keys(finalScreenshots).map((key) =>
          uploadSnapshot(key as `${directionType}_${backgroundType}`)
        );

        await Promise.all(fileUploads);
        setSubmissionError(null);
        setProgressStep("uploading_data");
      } catch (error) {
        ExceptionUtils.reportException(error, "error", {
          hint: "Failed to upload annotation images",
        });
        setSubmissionError("uploading_images");
      }
    };

    void performUploads();
  }, [progressStep]);

  const goBackToMyTasks = useCallback(() => {
    navigate(
      WorkflowTasksRoutePath.toLinkParts({
        queryParams: { tab: "my-tasks" },
      })
    );
  }, []);

  const mutation = useMutation({
    meta: { hideErrorToasts: true },
    mutationFn: async (
      formValues: Parameters<typeof FormSubmissionAdapter.createOne>[0]["data"]
    ) => {
      const {
        data: { id, ...restResponse },
      } = await FormSubmissionAdapter.createOne({ data: formValues });

      return { id, ...restResponse };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Resources.WORKFLOW] });
      queryClient.invalidateQueries({
        queryKey: [Resources.WORKFLOW, "tasks"],
      });
      queryClient.invalidateQueries({
        queryKey: ["forms", "submissions"],
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.FORM_SUBMISSION],
      });
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: [Resources.WORKFLOW, "tasks"],
        });
        queryClient.refetchQueries({
          queryKey: [Resources.WORKFLOW],
        });
      }, 4000);

      toast.push({
        intent: "success",
        description: "Successfully submitted annotation.",
      });

      goBackToMyTasks();
    },
    onError: (err) => {
      const typedError = err as HTTPResponseErrorProps;
      const isTaskStatusError =
        typedError?.responseBodyJson &&
        "error" in typedError.responseBodyJson &&
        typeof typedError.responseBodyJson.error === "string" &&
        typedError.responseBodyJson.error
          .trim()
          .toLowerCase()
          .includes(
            "Workflow task must have a status of open or overdue".toLowerCase()
          );

      if (isTaskStatusError) {
        if (hasRetriedOpenTaskRef.current) {
          setSubmissionError("task_already_completed");
          return;
        }
        if (!workflowId) {
          setSubmissionError("task_already_completed");
          return;
        }
        void (async () => {
          try {
            const listResponse = await WorkflowTaskAdapter.actions.getList({
              filters: {
                workflow_id: workflowId,
                status: "open",
                type: "submit_form",
              },
              pageSize: 2,
            });
            const tasks = listResponse?.data ?? [];
            if (tasks.length !== 1) {
              setSubmissionError("task_already_completed");
              return;
            }
            const alternativeTaskId = tasks[0].id;
            const formValues = prepareFormSubmissionAnswers();
            if (!formValues) {
              setSubmissionError("task_already_completed");
              return;
            }
            hasRetriedOpenTaskRef.current = true;
            mutation.mutate({
              ...formValues,
              workflow_task_id: alternativeTaskId,
            });
          } catch {
            setSubmissionError("task_already_completed");
          }
        })();
        return;
      }

      toast.push({
        intent: "error",
        description: "Failed to submit annotation",
      });
      setSubmissionError("uploading_data");
      ExceptionUtils.reportException(err, "error", {
        hint: "Failed to submit annotation form data",
      });
    },
  });

  useEffect(() => {
    if (progressStep !== "uploading_data") return;

    if (
      Object.values(uploadedScreenshotRefs).filter((v) => v !== null).length !==
      4
    )
      return;

    const formAnswers = prepareFormSubmissionAnswers();

    mutation.mutate(formAnswers);

    setProgressStep("done");
  }, [progressStep, uploadedScreenshotRefs]);

  /**
   * Handles retrying the submission process from the step that failed.
   * If image upload failed, retries from that step.
   * If form data upload failed, retries from that step.
   */
  const handleRetrySubmission = () => {
    if (!submissionError) return;

    // Clear the error state and allow fetching an alternative open task again
    setSubmissionError(null);
    hasRetriedOpenTaskRef.current = false;

    // Retry from the failed step
    if (submissionError === "uploading_images") {
      setProgressStep("uploading_images");
    } else if (submissionError === "uploading_data") {
      setProgressStep("uploading_data");
    }
  };

  /**
   * Resets the canvas to its original state by removing all marking objects
   * created during the screenshot generation process and resetting the view.
   */
  const resetCanvasToOriginalState = useCallback(() => {
    if (!fabricCanvas) return;

    // Remove all marking objects (lines drawn for the final screenshot)
    const markingObjects = fabricCanvas
      .getObjects()
      .filter((obj) => obj.get("id")?.toString().startsWith(markingsPrefix));
    markingObjects.forEach((obj) => fabricCanvas.remove(obj));

    // Reset the canvas view (zoom and pan)
    resetView(fabricCanvas);
  }, [fabricCanvas]);

  return (
    <Page
      title="Annotation Details"
      breadcrumbs={breadcrumbs}
      footer={
        <Footer>
          <Button
            disabled={isSaveButtonDisabled}
            variant="primary"
            onClick={async () => {
              setIsSaving(true);

              for (const direction of ["left", "right"] as directionType[]) {
                for (const background of [
                  "regular",
                  "uv",
                ] as backgroundType[]) {
                  await generateScreenshotMarkings({
                    direction,
                    background,
                  });
                }
              }
            }}
          >
            Save
          </Button>
        </Footer>
      }
    >
      <Dialog
        isOpen={isSaving}
        onClose={() => {
          if (submissionError) {
            setIsSaving(false);
            setSubmissionError(null);
          }
        }}
        title={submissionError ? "Submission Failed" : "Saving annotation..."}
        hideCloseButton
      >
        <Stepper
          isFluid
          steps={[
            {
              label: "Generating Annotation Images",
              intent: activeStepOfSubmission > 1 ? "success" : undefined,
              isBusy: activeStepOfSubmission === 1 && !submissionError,
            },
            {
              label: "Uploading Annotation Images",
              intent:
                submissionError === "uploading_images"
                  ? "error"
                  : activeStepOfSubmission > 2
                    ? "success"
                    : undefined,
              isBusy:
                activeStepOfSubmission === 2 &&
                submissionError !== "uploading_images",
            },
            {
              label: "Uploading Measurement Data",
              intent:
                submissionError &&
                ["uploading_data", "task_already_completed"].includes(
                  submissionError
                )
                  ? "error"
                  : activeStepOfSubmission > 3
                    ? "success"
                    : undefined,
              isBusy:
                activeStepOfSubmission === 3 &&
                submissionError !== "uploading_data",
            },
          ]}
          activeStep={activeStepOfSubmission}
          orientation="vertical"
        />

        {submissionError === "task_already_completed" && (
          <Banner
            titleText="This annotation is already completed by another user."
            description="Please choose a different annotation task."
            color="warning"
            variant="warning"
            isDismissable={false}
            style={{ marginTop: 16 }}
          />
        )}

        {submissionError && (
          <div
            style={{
              marginTop: 16,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            {["uploading_images", "uploading_data"].includes(
              submissionError
            ) && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsSaving(false);
                    setSubmissionError(null);
                    resetCanvasToOriginalState();
                  }}
                >
                  Edit Annotation
                </Button>
                <Button
                  variant="primary"
                  onClick={handleRetrySubmission}
                  slotLeft={<Icon variant="arrow-counter-clockwise" />}
                >
                  Retry
                </Button>
              </>
            )}
            {submissionError === "task_already_completed" && (
              <Button
                variant="primary"
                onClick={goBackToMyTasks}
                slotLeft={<Icon variant="arrow-circle-left" />}
              >
                Return to My Tasks
              </Button>
            )}
          </div>
        )}
      </Dialog>

      <Card>
        <div style={{ display: "flex", flexDirection: "row", gap: 32 }}>
          <div style={{ flexBasis: "450px" }}>
            {!activeMeasurement &&
              directionKeys.map((direction) => (
                <div
                  key={direction}
                  style={{ marginBottom: 16 }}
                >
                  <KeyValueList
                    data={measurementKeys.map((measurementKey) => ({
                      title: (
                        <div style={{ flexGrow: 1 }}>
                          {toTitleCaseString(measurementKey)} (
                          {toTitleCaseString(direction)})
                        </div>
                      ),
                      value: (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 8,
                            justifyContent: "flex-end",
                          }}
                        >
                          {renderValue(
                            measurements[`${direction}_${measurementKey}`]
                          )}
                          <DropdownMenu
                            options={[
                              {
                                label: "Annotate",
                                onClick: () =>
                                  setActiveMeasurement({
                                    measurement: measurementKey,
                                    direction: direction,
                                    measurementPoints: [],
                                    topTickY: -10000,
                                    bottomTickY: -10000,
                                    topTickX: 0,
                                    bottomTickX: 0,
                                    topTickValue: 0,
                                    bottomTickValue: 0,
                                    aggregateFunction:
                                      measurementKey === "water"
                                        ? "minimum"
                                        : "average",
                                  }),
                              },
                              {
                                label: "Set as 0",
                                onClick: () => {
                                  const key =
                                    `${direction}_${measurementKey}` as `${directionType}_${measurementsType}`;
                                  updateMeasurement(key, 0);
                                  setMeasurementData(key, null);
                                },
                              },
                              {
                                label: "Mark as Error",
                                onClick: () => {
                                  const key =
                                    `${direction}_${measurementKey}` as `${directionType}_${measurementsType}`;
                                  updateMeasurement(key, "error");
                                  setMeasurementData(key, null);
                                },
                                variant: "danger",
                              },
                            ]}
                          >
                            <Button
                              iconProps={{ size: 18 }}
                              variant="outline"
                              icon="caret-down"
                              iconPosition="right"
                            >
                              Measure
                            </Button>
                          </DropdownMenu>
                        </div>
                      ),
                    }))}
                    variant="shaded"
                  />
                </div>
              ))}

            {!activeMeasurement && hasIntegrityIssue && (
              <Banner
                titleText="Data Integrity Issue"
                color="error"
                variant="error"
                description="Each side must have at least one non-zero measured value. If the tube is missing, mark all measurements as Error for that tube. Please review your measurements."
              />
            )}

            {activeMeasurement && (
              <div>
                <div style={{ marginBottom: 24, fontSize: "20px" }}>
                  Measuring{" "}
                  <strong>
                    {toTitleCaseString(activeMeasurement.measurement)}
                  </strong>{" "}
                  on{" "}
                  <strong>
                    {toTitleCaseString(activeMeasurement.direction)}
                  </strong>{" "}
                  tube
                </div>
                <Stepper
                  isFluid
                  style={{ marginBottom: 16 }}
                  steps={[
                    {
                      label:
                        activeMeasurement.measurement === "water"
                          ? "Zoom in and draw the meniscus"
                          : "Zoom in and draw the layer",
                      intent: activeStep > 1 ? "success" : undefined,
                      description:
                        activeStep === 1
                          ? `In order to draw the meniscus, click "Enable Drawing" and click and drag on the image to draw.
                          When finished, click "Next".`
                          : undefined,
                    },
                    {
                      label: "Drag and Place Tick Indicators",
                      intent: activeStep > 2 ? "success" : "default",
                      description:
                        activeStep === 2
                          ? `Now press "Place Top Tick" and "Place Bottom Tick" buttons for the indicators to appear.
                          Drag and place them over the closest vertical lines above and below the drawn meniscus respectively.
                          When finished, click "Next".`
                          : undefined,
                    },
                    {
                      label: "Enter Top Tick and Bottom Tick values",
                      intent: activeStep > 3 ? "success" : "default",
                      description:
                        activeStep === 3
                          ? `Read the values on the tube where you've placed the top and bottom ticks.
                          Enter those values in the fields below. When finished, click "Finish".`
                          : undefined,
                    },
                  ]}
                  activeStep={activeStep}
                  orientation="vertical"
                />

                {activeStep === 3 && (
                  <Card>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        gap: "8px 16px",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Icon variant="caret-line-up" />
                        <span>Top Tick Value:</span>
                      </div>
                      <AnnotationNumberInput
                        label="Top Tick Value"
                        value={activeMeasurement?.topTickValue ?? 0}
                        onChange={(topTickValue) => {
                          setActiveMeasurement({
                            ...activeMeasurement,
                            topTickValue,
                          });
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Icon variant="caret-line-down" />
                        <span>Bottom Tick Value:</span>
                      </div>

                      <AnnotationNumberInput
                        label="Bottom Tick Value"
                        value={activeMeasurement?.bottomTickValue ?? 0}
                        onChange={(bottomTickValue) => {
                          setActiveMeasurement({
                            ...activeMeasurement,
                            bottomTickValue,
                          });
                        }}
                      />
                    </div>
                  </Card>
                )}

                <div
                  style={{
                    marginTop: 16,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    variant="error-outline"
                    onClick={() => {
                      clearActiveMeasurement();
                      resetSteps();
                    }}
                    slotLeft={<Icon variant="x-circle" />}
                  >
                    Exit
                  </Button>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                    }}
                  >
                    {activeStep > 1 && (
                      <Button
                        variant="outline"
                        onClick={goToPreviousStep}
                        slotLeft={<Icon variant="caret-left" />}
                      >
                        Previous Step
                      </Button>
                    )}
                    {activeStep < 3 && (
                      <Button
                        variant="primary"
                        onClick={goToNextStep}
                        slotRight={<Icon variant="caret-right" />}
                        disabled={isNextButtonDisabled}
                      >
                        Next
                      </Button>
                    )}
                    {activeStep === 3 && (
                      <Button
                        variant="primary"
                        onClick={() => {
                          if (!activeMeasurement) return;
                          const drawnMeniscusYValue = aggregateFunctions[
                            activeMeasurement.aggregateFunction
                          ](activeMeasurement.measurementPoints);

                          if (
                            activeMeasurement.bottomTickY ===
                            activeMeasurement.topTickY
                          ) {
                            toast.push({
                              intent: "error",
                              description:
                                "Top and Bottom Ticks have the same value. Please try again.",
                            });

                            resetSteps();
                            return;
                          }

                          // Check if both ticks are on the same side of the drawn line
                          const bothTicksAboveLine =
                            activeMeasurement.topTickY < drawnMeniscusYValue &&
                            activeMeasurement.bottomTickY < drawnMeniscusYValue;
                          const bothTicksBelowLine =
                            activeMeasurement.topTickY > drawnMeniscusYValue &&
                            activeMeasurement.bottomTickY > drawnMeniscusYValue;

                          if (bothTicksAboveLine || bothTicksBelowLine) {
                            toast.push({
                              intent: "error",
                              description:
                                "Both ticks are placed on the same side of the drawn line. Please place one tick above and one below the line.",
                            });

                            resetSteps();
                            return;
                          }

                          if (activeMeasurement.topTickValue === 0) {
                            toast.push({
                              intent: "error",
                              description: "Top tick value should be positive.",
                            });

                            resetSteps();
                            return;
                          }

                          const scaledMeniscusYValue =
                            (activeMeasurement.bottomTickY -
                              drawnMeniscusYValue) /
                            (activeMeasurement.bottomTickY -
                              activeMeasurement.topTickY);

                          const realValue =
                            (activeMeasurement.topTickValue -
                              activeMeasurement.bottomTickValue) *
                              scaledMeniscusYValue +
                            activeMeasurement.bottomTickValue;

                          if (realValue < 0) {
                            toast.push({
                              intent: "error",
                              description:
                                "Calculated value is negative. Please try again.",
                            });

                            resetSteps();
                            return;
                          }

                          if (
                            activeMeasurement.topTickValue ===
                            activeMeasurement.bottomTickValue
                          ) {
                            toast.push({
                              intent: "error",
                              description:
                                "Top and Bottom Tick values are the same. Please try again.",
                            });

                            resetSteps();
                            return;
                          }

                          updateMeasurement(
                            `${activeMeasurement.direction}_${activeMeasurement.measurement}`,
                            realValue
                          );

                          // Take screenshot and save tick data for this specific measurement
                          if (fabricCanvas && activeMeasurement) {
                            const measurementKey = `${activeMeasurement.direction}_${activeMeasurement.measurement}`;

                            // Save entire measurement data
                            setMeasurementData(
                              measurementKey as `${directionType}_${measurementsType}`,
                              activeMeasurement
                            );
                          }

                          clearActiveMeasurement();
                          resetSteps();
                        }}
                        slotRight={<Icon variant="check-circle" />}
                        disabled={isNextButtonDisabled}
                      >
                        Finish
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div style={{ flexGrow: 1 }}>
            <NewAnnotationTool />
          </div>
        </div>
      </Card>
    </Page>
  );
};
